import { FormEvent, useEffect, useState } from 'react';
import { Copy, Eye, RefreshCw } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { TotpInput } from '../../components/ui/TotpInput';
import { useEnvironment } from '../../context/EnvironmentContext';
import { useAuth } from '../../context/AuthContext';
import type { WebhookLog } from '../../api/types';

export function DeveloperPage() {
  const { environment } = useEnvironment();
  const { merchant, user } = useAuth();
  const [publicKey, setPublicKey] = useState('');
  const [masked, setMasked] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const totpEnabled = user?.totp_enabled === true;

  const loadKeys = () => {
    void portalApi.apiKeys(environment).then((r) => {
      setPublicKey(r.public_key);
      setMasked(r.secret_key_masked);
      setRevealed(null);
    });
  };

  const loadLogs = () => {
    void portalApi.webhookLogs().then((r) => setLogs(r.logs ?? []));
  };

  useEffect(() => {
    loadKeys();
    loadLogs();
    if (merchant?.webhook_url) setWebhookUrl(merchant.webhook_url);
  }, [environment, merchant?.webhook_url]);

  const copy = (text: string) => void navigator.clipboard.writeText(text);

  const reveal = async (e: FormEvent) => {
    e.preventDefault();
    if (!totpEnabled || totp.length !== 6) {
      setErr('Enable 2FA in Security and enter your 6-digit code');
      return;
    }
    try {
      const r = await portalApi.revealSecret(password, totp, environment);
      setRevealed(r.secret_key ?? null);
      setPassword('');
      setTotp('');
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    }
  };

  const regenerate = async () => {
    if (!totpEnabled || totp.length !== 6) {
      setErr('Enable 2FA in Security and enter your 6-digit code');
      return;
    }
    if (!confirm('Regenerate API keys? Old secret will stop working.')) return;
    try {
      const r = await portalApi.regenerateKeys(totp, environment);
      setPublicKey(r.public_key);
      setRevealed(r.secret_key ?? null);
      setTotp('');
      setMsg('Keys regenerated');
    } catch (ex) {
      setErr(getApiErrorMessage(ex));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h2 className="font-display text-2xl">Developer hub</h2>
      {err ? <p className="text-sm text-onyx-danger">{err}</p> : null}
      {msg ? <p className="text-sm text-onyx-success">{msg}</p> : null}

      <Card title="API keys">
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-onyx-muted mb-1">Public key</p>
            <div className="flex gap-2 items-center font-mono text-xs break-all">
              {publicKey}
              <button type="button" onClick={() => copy(publicKey)} className="text-onyx-gold"><Copy size={14} /></button>
            </div>
          </div>
          <div>
            <p className="text-onyx-muted mb-1">Secret key</p>
            <p className="font-mono text-xs">{revealed ?? masked}</p>
          </div>
          {totpEnabled ? (
            <div>
              <p className="text-onyx-muted mb-1 text-xs">2FA code (required for reveal / regenerate)</p>
              <TotpInput value={totp} onChange={setTotp} />
            </div>
          ) : (
            <p className="text-xs text-onyx-warn">Enable two-factor authentication in Security before revealing or rotating keys.</p>
          )}
          <form onSubmit={reveal} className="flex gap-2 mt-2">
            <Input type="password" placeholder="Password to reveal" value={password} onChange={(e) => setPassword(e.target.value)} className="flex-1" />
            <Button type="submit" variant="ghost" disabled={!totpEnabled}><Eye size={16} /> Reveal</Button>
          </form>
          <Button variant="ghost" onClick={regenerate} disabled={!totpEnabled}><RefreshCw size={16} /> Regenerate keys</Button>
        </div>
      </Card>

      <Card title="Webhooks">
        <form
          className="flex gap-2 mb-4"
          onSubmit={async (e) => {
            e.preventDefault();
            await portalApi.updateWebhook(webhookUrl);
            setMsg('Webhook URL saved');
          }}
        >
          <Input placeholder="https://your-site.com/webhooks/onyx" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} className="flex-1" />
          <Button type="submit">Save</Button>
        </form>
        <Button
          variant="ghost"
          onClick={async () => {
            try {
              const r = await portalApi.testWebhook();
              setMsg(`Test ping: HTTP ${r.status_code} (${r.response_ms}ms)`);
              loadLogs();
            } catch (ex) {
              setErr(getApiErrorMessage(ex));
            }
          }}
        >
          Test webhook
        </Button>
        <table className="data-table mt-4">
          <thead>
            <tr>
              <th>Time</th>
              <th>URL</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id}>
                <td>{new Date(l.created_at).toLocaleString()}</td>
                <td className="cell-mono text-xs">{l.url ?? '—'}</td>
                <td>{l.status_code ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
